import { Checkbox, Flex, useToast } from "@chakra-ui/react";
import { type FormikHelpers, useFormik } from "formik";
import { useCallback, useState } from "react";
import * as Yup from "yup";

import { postalCodeYupSchema } from "@equidefi/portals/helpers/postalCode";
import { usePostalCodeExcluded } from "@equidefi/portals/hooks/usePostalCode";
import type { DTO } from "@equidefi/shared/types/dto";
import { Heading, Input, Select } from "@equidefi/ui";

// @ts-ignore
import { COUNTRY_OPTIONS } from "@equidefi/constants/countries";
import { MESSAGE } from "@equidefi/constants/forms";
import { ENTITY_SUB_TYPE_OPTIONS } from "@equidefi/constants/profile";
import { SavedProfileFormContainer } from "./SavedProfileFormContainer";

const schema = Yup.object({
  ta_sub_type: Yup.string().nullable().required(MESSAGE.REQUIRED("Entity Sub-type")),
  company: Yup.string().trim().nullable().required(MESSAGE.REQUIRED("Entity Name")),
  ein: Yup.string().trim().nullable().required(MESSAGE.REQUIRED("EIN")),
  entity_address_street_1: Yup.string().trim().nullable().required(MESSAGE.REQUIRED("Street Address")),
  entity_address_street_2: Yup.string().trim().nullable(),
  entity_address_city: Yup.string().trim().nullable().required(MESSAGE.REQUIRED("City")),
  entity_address_state: Yup.string().trim().nullable().required(MESSAGE.REQUIRED("State")),
  entity_address_postal_code: postalCodeYupSchema("entity_address_country_code", MESSAGE.REQUIRED("Postal Code")),
  entity_address_country_code: Yup.string().trim().nullable().required(MESSAGE.REQUIRED("Country")),
});

export type EntityAccountTypeFormValues = {
  ta_sub_type: string | null;
  company: string;
  ein: string;
  entity_address_street_1: string;
  entity_address_street_2: string | null;
  entity_address_city: string;
  entity_address_state: string;
  entity_address_postal_code: string;
  entity_address_country_code: string;
};

type EntityAccountTypeFormProps = {
  profile: DTO.SavedProfile;
  onSave: (values: EntityAccountTypeFormValues) => Promise<void>;
};

const getInitialValues = (profile: DTO.SavedProfile): EntityAccountTypeFormValues => ({
  ta_sub_type: profile.ta_sub_type ?? null,
  company: profile.company ?? "",
  ein: profile.ein ?? "",
  entity_address_street_1: profile.entity_address_street_1 ?? "",
  entity_address_street_2: profile.entity_address_street_2 ?? null,
  entity_address_city: profile.entity_address_city ?? "",
  entity_address_state: profile.entity_address_state ?? "",
  entity_address_postal_code: profile.entity_address_postal_code ?? "",
  entity_address_country_code: profile.entity_address_country_code ?? "",
});

const normalizeValues = (values: EntityAccountTypeFormValues): EntityAccountTypeFormValues => ({
  ...values,
  company: values.company.trim(),
  ein: values.ein.trim(),
  entity_address_street_1: values.entity_address_street_1.trim(),
  entity_address_street_2: values.entity_address_street_2?.trim() || null,
  entity_address_city: values.entity_address_city.trim(),
  entity_address_state: values.entity_address_state.trim(),
  entity_address_postal_code: values.entity_address_postal_code.trim(),
  entity_address_country_code: values.entity_address_country_code.trim(),
});

export function EntityAccountTypeForm({ profile, onSave }: EntityAccountTypeFormProps) {
  const toast = useToast();
  const [isEinVisible, setIsEinVisible] = useState(false);

  const onSubmit = useCallback(
    async (values: EntityAccountTypeFormValues, { setSubmitting }: FormikHelpers<EntityAccountTypeFormValues>) => {
      try {
        await onSave(normalizeValues(values));
      } catch (error) {
        console.error(error);
        toast({
          status: "error",
          description: "There was an error trying to save your entity account type.",
        });
      } finally {
        setSubmitting(false);
      }
    },
    [onSave, toast],
  );

  const { handleSubmit, setFieldValue, getFieldProps, errors, values, touched, isValid, isSubmitting } =
    useFormik<EntityAccountTypeFormValues>({
      initialValues: getInitialValues(profile),
      validationSchema: schema,
      onSubmit,
      validateOnChange: true,
      validateOnBlur: true,
      validateOnMount: true,
    });

  const hidePostalCode = usePostalCodeExcluded(
    values.entity_address_country_code,
    values.entity_address_postal_code,
    () => setFieldValue("entity_address_postal_code", ""),
  );

  return (
    <SavedProfileFormContainer
      profile={profile}
      title="Entity Details"
      onSave={handleSubmit}
      isLoading={isSubmitting}
      isDisabled={!isValid}>
      <Select<{ ta_sub_type: string }>
        name="ta_sub_type"
        label="Entity Sub-type"
        isRequired
        placeholder="Select Sub-type"
        options={ENTITY_SUB_TYPE_OPTIONS}
        value={ENTITY_SUB_TYPE_OPTIONS.find((t) => t.value === values.ta_sub_type) ?? undefined}
        onChange={(option) => setFieldValue("ta_sub_type", option?.value ?? null)}
        error={touched.ta_sub_type ? errors.ta_sub_type : undefined}
        isInvalid={!!errors.ta_sub_type && touched.ta_sub_type}
      />

      <Input
        label="Entity Name"
        isRequired
        error={touched.company ? errors.company : undefined}
        isInvalid={!!errors.company && touched.company}
        {...getFieldProps("company")}
      />

      <Flex gap={3} align="flex-end">
        <Input
          label="EIN"
          isRequired
          type={isEinVisible ? "text" : "password"}
          error={touched.ein ? errors.ein : undefined}
          isInvalid={!!errors.ein && touched.ein}
          {...getFieldProps("ein")}
        />
        <Checkbox
          mb={2}
          isChecked={isEinVisible}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setIsEinVisible((e.target as any).checked)}
          size="sm"
          colorScheme="purple"
          whiteSpace="nowrap">
          Show EIN
        </Checkbox>
      </Flex>

      <Heading textStyle="h2" m={0} mt={4} mb={2} fontWeight="700">
        Company Address
      </Heading>

      <Input
        label="Street Address"
        isRequired
        error={touched.entity_address_street_1 ? errors.entity_address_street_1 : undefined}
        isInvalid={!!errors.entity_address_street_1 && touched.entity_address_street_1}
        {...getFieldProps("entity_address_street_1")}
      />

      <Input
        label="Street Address 2 (Unit #)"
        error={touched.entity_address_street_2 ? errors.entity_address_street_2 : undefined}
        isInvalid={!!errors.entity_address_street_2 && touched.entity_address_street_2}
        {...getFieldProps("entity_address_street_2")}
      />

      <Input
        label="City"
        isRequired
        error={touched.entity_address_city ? errors.entity_address_city : undefined}
        isInvalid={!!errors.entity_address_city && touched.entity_address_city}
        {...getFieldProps("entity_address_city")}
      />

      <Input
        label="State"
        isRequired
        error={touched.entity_address_state ? errors.entity_address_state : undefined}
        isInvalid={!!errors.entity_address_state && touched.entity_address_state}
        {...getFieldProps("entity_address_state")}
      />

      {!hidePostalCode && (
        <Input
          label="Postal Code"
          isRequired
          error={touched.entity_address_postal_code ? errors.entity_address_postal_code : undefined}
          isInvalid={!!errors.entity_address_postal_code && touched.entity_address_postal_code}
          {...getFieldProps("entity_address_postal_code")}
        />
      )}

      <Select<{ entity_address_country_code: string }>
        name="entity_address_country_code"
        label="Country"
        isRequired
        options={COUNTRY_OPTIONS}
        value={COUNTRY_OPTIONS.find((o) => o.value === values.entity_address_country_code) ?? undefined}
        onChange={(option) => setFieldValue("entity_address_country_code", option?.value ?? null)}
        error={touched.entity_address_country_code ? errors.entity_address_country_code : undefined}
        isInvalid={!!errors.entity_address_country_code && touched.entity_address_country_code}
      />
    </SavedProfileFormContainer>
  );
}
